import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import { LoginPage, RegisterPage } from "./pages/AuthPages";
import Dashboard from "./pages/Dashboard";
import ClassBrowser from "./pages/ClassBrowser";
import ClassDetail from "./pages/ClassDetail";
import VideoPlayer from "./pages/VideoPlayer";
import SubscriptionPage from "./pages/SubscriptionPage";
import ProfilePage from "./pages/ProfilePage";
import ShopPage from "./pages/ShopPage";
import CartPage from "./pages/CartPage";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import InstructorsPage from "./pages/InstructorsPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOrderDetail from "./pages/AdminOrderDetail";
import SettingsPage from "./pages/SettingsPage";
import TheMethodPage from "./pages/TheMethodPage";
import ProgramsPage from "./pages/ProgramsPage";
import InstructorPortal from "./pages/InstructorPortal";
import ReferralPage from "./pages/ReferralPage";

import "./index.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LandingPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } 
      />

      {/* Shop Routes - Public */}
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/:productId" element={<ShopPage />} />
      <Route path="/instructors" element={<InstructorsPage />} />
      <Route path="/the-method" element={<TheMethodPage />} />
      
      {/* Cart & Orders - Protected */}
      <Route 
        path="/cart" 
        element={
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/order/success" 
        element={
          <ProtectedRoute>
            <OrderSuccessPage />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute>
            <ClassBrowser />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/class/:classId" 
        element={
          <ProtectedRoute>
            <ClassDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/watch/:classId" 
        element={
          <ProtectedRoute>
            <VideoPlayer />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profile" 
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/orders/:orderId" 
        element={
          <ProtectedRoute>
            <AdminOrderDetail />
          </ProtectedRoute>
        } 
      />

      {/* Programs Routes */}
      <Route 
        path="/programs" 
        element={
          <ProtectedRoute>
            <ProgramsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/programs/:programId" 
        element={
          <ProtectedRoute>
            <ProgramsPage />
          </ProtectedRoute>
        } 
      />

      {/* Referral Program */}
      <Route 
        path="/referrals" 
        element={
          <ProtectedRoute>
            <ReferralPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/refer" 
        element={<Navigate to="/referrals" replace />}
      />

      {/* Instructor Portal */}
      <Route 
        path="/instructor" 
        element={<InstructorPortal />}
      />
      <Route 
        path="/instructor-portal" 
        element={<Navigate to="/instructor" replace />}
      />
      <Route 
        path="/instructor/login" 
        element={<InstructorPortal />}
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#121212',
                color: '#ffffff',
                border: '1px solid #27272A'
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
